/**
 * Gating Policy
 *
 * Decide quando chamar a API facial, quando bloquear revalidação da mesma face,
 * e quando aplicar cooldown de retry.
 *
 * Regras:
 * - recognized (recorded true/false) => identified, bloqueia repetição.
 * - unrecognized => retrying com cooldown local.
 * - no_face => reseta, volta a esperar.
 * - Durante recognizing, não enfileirar nova chamada.
 * - Só rearma identified se face_changed ou ausência confirmada.
 */

import type { FaceBox } from "./stability-tracker";

export type GatingAction =
  | { type: "call_api"; box: FaceBox }
  | { type: "block_same_face" }
  | { type: "wait" }
  | { type: "retry"; remainingMs: number }
  | { type: "reset" };

export interface GatingPolicyOptions {
  retryCooldownMs?: number;
}

export class GatingPolicy {
  private retryCooldownMs: number;
  private retryUntil = 0;
  private identifiedBox: FaceBox | null = null;
  private lastSentBox: FaceBox | null = null;
  private isRecognizing = false;
  private nowFn: () => number;

  constructor(options: GatingPolicyOptions = {}, nowFn: () => number = Date.now) {
    this.retryCooldownMs = options.retryCooldownMs ?? 3000;
    this.nowFn = nowFn;
  }

  /**
   * Avalia evento de estabilidade e decide ação.
   */
  onStabilityEvent(
    event: { type: "stable"; box: FaceBox } | { type: "unstable" } | { type: "absent" } | { type: "multiple" }
  ): GatingAction {
    const now = this.nowFn();

    if (event.type === "absent") {
      this.identifiedBox = null;
      this.isRecognizing = false;
      return { type: "reset" };
    }

    if (event.type === "multiple" || event.type === "unstable") {
      return { type: "wait" };
    }

    // stable
    if (this.isRecognizing) {
      return { type: "wait" };
    }

    if (now < this.retryUntil) {
      return { type: "retry", remainingMs: this.retryUntil - now };
    }

    if (this.identifiedBox && !this.faceChanged(this.identifiedBox, event.box)) {
      return { type: "block_same_face" };
    }

    // Nova face ou rearme após ausência
    return { type: "call_api", box: event.box };
  }

  onApiResult(result: { status: "recognized" | "unrecognized" | "blocked" | "no_face"; recorded: boolean }): void {
    if (result.status === "recognized" || result.status === "blocked") {
      // recognized/blocked (recorded:true OU recorded:false) entra em identified e bloqueia repetição
      // da mesma face: um profissional bloqueado (4.5) não deve ser reenviado a cada quadro.
      this.isRecognizing = false;
      // Guarda a box da face que foi enviada para API como referência de bloqueio.
      this.identifiedBox = this.lastSentBox ?? { xCenter: -1, yCenter: -1, width: -1, height: -1 };
    } else if (result.status === "unrecognized") {
      // unrecognized sempre entra em retrying com cooldown local.
      this.isRecognizing = false;
      this.retryUntil = this.nowFn() + this.retryCooldownMs;
    } else if (result.status === "no_face") {
      this.isRecognizing = false;
      this.identifiedBox = null;
      this.lastSentBox = null;
    }
  }

  onApiCallStarted(box: FaceBox): void {
    this.isRecognizing = true;
    this.lastSentBox = box;
  }

  onApiCallFinished(): void {
    this.isRecognizing = false;
  }

  reset(): void {
    this.identifiedBox = null;
    this.isRecognizing = false;
    this.retryUntil = 0;
  }

  private faceChanged(a: FaceBox, b: FaceBox): boolean {
    const centerDelta = Math.hypot(a.xCenter - b.xCenter, a.yCenter - b.yCenter);
    const sizeDelta = Math.abs(a.width - b.width) + Math.abs(a.height - b.height);
    // Usamos os mesmos limiares da POC
    return centerDelta > 0.12 || sizeDelta > 0.20;
  }
}
